import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { StageBadge } from '@/components/shared/StageBadge'
import { StatusBadge } from '@/components/shared/StatusBadge'
import { FrequencyBadge } from '@/components/shared/FrequencyBadge'
import { formatCurrency, formatDate } from '@/lib/utils'
import { useBasePath } from '@/lib/paths'
import { LicenseDetailed, LicenseRecordSummary } from '@/types'
import { Plus, Calendar, AlertCircle, ClipboardCheck, ChevronRight, Award, Info, FileText } from 'lucide-react'
import { Link } from '@inertiajs/react'


type Props = {
  license: LicenseDetailed
  type: { id: string; name: string; gov_fee_cents: number; service_fee_cents: number; frequency: string | null; validity_period_months: number | null }
}

export default function LicensesShow({ license, type }: Props) {
  const basePath = useBasePath()

  return (
    <DashboardLayout title={license.display_name} breadcrumbs={[{ label: 'Licenses', href: 'licenses' }, { label: license.display_name }]}>
      <div>
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-xl font-semibold tracking-tight">{license.display_name}</h1>
            <StatusBadge status={license.status} />
            <FrequencyBadge frequency={license.frequency} />
            {license.perpetual && <Badge variant="outline" className="font-normal text-xs">Perpetual</Badge>}
          </div>
          <p className="text-sm text-muted-foreground">{license.business_name} · {license.jurisdiction_label || license.type_code}</p>
          <div className="flex items-center gap-2 mt-3">
            <Button asChild size="sm" variant="outline">
              <Link href={`${basePath}/licenses/${license.id}/documents`}>
                <FileText className="w-3.5 h-3.5 mr-1.5" />
                Documents
              </Link>
            </Button>
          </div>
        </div>

        {/* Issuance Banner */}
        {license.issuance && (
          <div className="flex items-center gap-3 px-4 py-3 bg-muted rounded-lg mb-8">
            <Award className="w-4 h-4 text-green-600 shrink-0" />
            <div className="text-sm">
              <span className="font-medium">Issued:</span> {formatDate(license.issuance.issued_date)}
              {license.issuance.expires_on ? ` · Expires: ${formatDate(license.issuance.expires_on)}` : ' · Perpetual'}
              {license.expired && <span className="text-red-600 font-medium ml-1">(Expired)</span>}
            </div>
          </div>
        )}

        {/* Custom Request */}
        {license.custom_request && (
          <div className="flex items-center gap-3 px-4 py-3 bg-muted rounded-lg mb-8">
            <Info className="w-4 h-4 text-blue-600 shrink-0" />
            <div className="text-sm">
              <span className="font-medium">Custom Request:</span> {license.custom_request.name}
              {license.custom_request.description && ` — ${license.custom_request.description}`}
            </div>
          </div>
        )}

        {/* Key Dates */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="border rounded-lg px-4 py-3">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Start Date</p>
            <p className="text-sm">{formatDate(license.start_date)}</p>
          </div>
          <div className="border rounded-lg px-4 py-3">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Next Due Date</p>
            <p className={`text-sm ${license.next_due_date && new Date(license.next_due_date) < new Date() ? 'text-red-600 font-medium' : ''}`}>
              {license.next_due_date ? formatDate(license.next_due_date) : '—'}
            </p>
          </div>
          <div className="border rounded-lg px-4 py-3">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1">Cost per Period</p>
            <p className="text-sm">{formatCurrency(type.gov_fee_cents + type.service_fee_cents)}</p>
          </div>
        </div>

        {/* License Records */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              License Records
            </h2>
            {license.status === 'active' && license.frequency && (
              <Button asChild variant="ghost" size="sm" className="h-7 text-xs">
                <Link href={`${basePath}/licenses/${license.id}/records/new`}>
                  <Plus className="w-3 h-3 mr-1" />
                  File Next Period
                </Link>
              </Button>
            )}
          </div>
          {license.records.length > 0 ? (
            <div className="border rounded-lg divide-y">
              {license.records.map(record => (
                <Link
                  key={record.id}
                  href={`${basePath}/licenses/${license.id}/records/${record.id}`}
                  className="flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <div>
                      <div className="text-sm font-medium text-foreground">
                        {formatDate(record.period_start_date)} – {formatDate(record.period_end_date)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Due: {formatDate(record.due_date)}
                        {record.overdue && (
                          <span className="text-red-600 font-medium ml-2 inline-flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            Overdue
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2.5">
                    <StageBadge stage={record.stage} variant="license" />
                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="border rounded-lg py-16 text-center">
              <ClipboardCheck className="w-6 h-6 mx-auto mb-3 text-muted-foreground/30" />
              <h3 className="text-sm font-medium text-foreground mb-1">No license records yet</h3>
              <p className="text-sm text-muted-foreground mb-4">File your first license period</p>
              {license.status === 'active' && (
                <Button asChild size="sm">
                  <Link href={`${basePath}/licenses/${license.id}/records/new`}>
                    <Plus className="w-3.5 h-3.5 mr-1.5" />
                    File Next Period
                  </Link>
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  )
}
